import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import RichTextEditor from "@/components/RichTextEditor";
import AutoResponseToggle from "@/components/leads/AutoResponseToggle";
import ProgramsManagerDialog from "@/components/leads/ProgramsManagerDialog";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Mail, Loader2, Edit3, Trash2, Eye, Save, Info, Plus, Layers, Building2, Copy, Upload, ListChecks, GripVertical } from "lucide-react";

const CAMPUSES = ["general", "santa_ana", "atenas", "grecia", "online"];

export default function LeadTemplates() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [observedPrograms, setObservedPrograms] = useState([]); // list of {program, count, campus} from historical leads
  const [managedPrograms, setManagedPrograms] = useState([]); // list of {id, campus, name, order} manually managed by admin
  const [programsManagerCampus, setProgramsManagerCampus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template being edited or new one
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [draftProgram, setDraftProgram] = useState("");
  const [draftCampus, setDraftCampus] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [livePreviewHtml, setLivePreviewHtml] = useState("");
  const [bulkSeedCampus, setBulkSeedCampus] = useState(null);
  const [bulkSeedText, setBulkSeedText] = useState("");
  const [bulkSeedRunning, setBulkSeedRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tpls, progs, mgr] = await Promise.all([
        api.get("/lead-templates"),
        api.get("/lead-programs").catch(() => ({ data: { items: [] } })),
        api.get("/campus-programs").catch(() => ({ data: [] })),
      ]);
      setItems(tpls.data.items || []);
      setObservedPrograms(progs.data.items || []);
      setManagedPrograms(Array.isArray(mgr.data) ? mgr.data : []);
    } catch {
      toast.error("Error loading templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group by campus
  const byCampus = useMemo(() => {
    const map = Object.fromEntries(CAMPUSES.map((c) => [c, []]));
    for (const it of items) {
      if (map[it.campus]) map[it.campus].push(it);
    }
    return map;
  }, [items]);

  const openEdit = (tpl) => {
    setEditing(tpl);
    setDraftSubject(tpl.subject);
    setDraftBody(tpl.body_html);
    setDraftProgram(tpl.program || "");
    setDraftCampus(tpl.campus);
  };

  const openNew = (campus, program = "") => {
    setEditing({ __new: true, campus, program: program || null });
    setDraftSubject("");
    setDraftBody("");
    setDraftProgram(program);
    setDraftCampus(campus);
  };

  const openDuplicate = (tpl) => {
    // Open editor as new but pre-fill subject/body with the source
    setEditing({ __new: true, campus: tpl.campus, program: null });
    setDraftSubject(tpl.subject);
    setDraftBody(tpl.body_html);
    setDraftProgram("");
    setDraftCampus(tpl.campus);
  };

  const openBulkSeed = (campus) => {
    setBulkSeedCampus(campus);
    setBulkSeedText("");
  };

  // Debounced live preview while editing
  useEffect(() => {
    if (!editing) { setLivePreviewHtml(""); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/lead-templates/preview", {
          campus: draftCampus,
          program: (draftProgram || "").trim() || null,
          subject: draftSubject,
          body_html: draftBody,
        });
        setLivePreviewHtml(data.html);
      } catch {}
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, draftBody, draftSubject, draftProgram, draftCampus]);

  const runBulkSeed = async () => {
    const campus = bulkSeedCampus;
    if (!campus) return;
    const lines = bulkSeedText.split("\n").map((s) => s.trim()).filter(Boolean);
    // Find existing programs for this campus to avoid duplicates
    const existingProgs = new Set(items.filter((it) => it.campus === campus && it.program).map((it) => it.program));
    const toCreate = lines.filter((p) => !existingProgs.has(p));
    if (toCreate.length === 0) {
      toast.info(t("template_bulk_seed_none"));
      return;
    }
    // Use campus-fallback as the base template (or hardcoded if none)
    const base = items.find((it) => it.campus === campus && !it.program);
    setBulkSeedRunning(true);
    try {
      for (const p of toCreate) {
        await api.post("/lead-templates", {
          campus,
          program: p,
          subject: base?.subject || "¡Hola {name}! Gracias por tu interés",
          body_html: base?.body_html || "<p>Hola {name}, gracias por contactarnos.</p>",
        });
      }
      toast.success(t("template_bulk_seed_done").replace("{n}", toCreate.length));
      setBulkSeedCampus(null);
      setBulkSeedText("");
      await load();
    } catch {
      toast.error("Error");
    } finally {
      setBulkSeedRunning(false);
    }
  };

  const closeEditor = () => {
    setEditing(null);
    setDraftSubject("");
    setDraftBody("");
    setDraftProgram("");
    setDraftCampus("");
  };

  const saveTemplate = async () => {
    if (!editing) return;
    if (!draftSubject.trim()) {
      toast.error(t("template_subject") + " *");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        campus: draftCampus,
        program: (draftProgram || "").trim() || null,
        subject: draftSubject,
        body_html: draftBody,
      };
      await api.post("/lead-templates", payload);
      toast.success(t("template_saved"));
      await load();
      closeEditor();
    } catch {
      toast.error("Error saving template");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!tpl.id) {
      // is_default = true → nothing to delete server-side
      return;
    }
    try {
      await api.delete(`/lead-templates/${tpl.id}`);
      toast.success(t("template_reset_done"));
      await load();
    } catch {
      toast.error("Error deleting template");
    }
  };

  const reorderTemplates = async (campus, orderedIds) => {
    // Optimistic UI: reorder items[] immediately so the drag feels instant.
    setItems((prev) => {
      const inCampus = prev.filter((t) => t.campus === campus);
      const outside = prev.filter((t) => t.campus !== campus);
      const fallback = inCampus.find((t) => !t.program);
      const programs = inCampus.filter((t) => !!t.program);
      // Build a lookup and reorder according to orderedIds
      const byId = Object.fromEntries(programs.map((t) => [t.id, t]));
      const reordered = orderedIds.map((id) => byId[id]).filter(Boolean);
      // Include any programs not in the orderedIds list (edge case, shouldn't happen)
      const missing = programs.filter((t) => !orderedIds.includes(t.id));
      return [...outside, ...(fallback ? [fallback] : []), ...reordered, ...missing];
    });
    try {
      await api.post("/lead-templates/reorder", { campus, template_ids: orderedIds });
      // No need to reload — server has the same order we already show.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error reordering");
      await load(); // rollback: fetch true server state
    }
  };

  const openPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.post(`/lead-templates/preview`, {
        campus: draftCampus,
        program: (draftProgram || "").trim() || null,
        subject: draftSubject,
        body_html: draftBody,
      });
      setPreviewHtml(data.html);
      setPreviewSubject(data.subject);
      setPreviewOpen(true);
    } catch {
      toast.error("Error generating preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  // Combined list: managed programs (source of truth) first, then observed programs seen in leads
  const programsForCampus = (campus) => {
    const managed = managedPrograms.filter((p) => p.campus === campus).map((p) => p.name);
    const observed = observedPrograms
      .filter((p) => p.campus === campus && !managed.includes(p.program))
      .map((p) => p.program);
    return [...managed, ...observed];
  };

  return (
    <div className="space-y-6" data-testid="lead-templates-page">
      <PageHeader title={t("lead_templates_title")} subtitle={t("lead_templates_subtitle")} icon={Mail} />

      <AutoResponseToggle />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-medium text-blue-900">{t("template_variables_help")}</div>
          <div className="text-xs text-blue-700 mt-1">{t("template_variables_note")}</div>
          <div className="text-xs text-blue-700 mt-2">{t("template_resolution_note")}</div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />Loading…
        </div>
      ) : (
        <div className="space-y-6" data-testid="templates-grid">
          {CAMPUSES.map((campus) => (
            <CampusSection
              key={campus}
              campus={campus}
              items={byCampus[campus] || []}
              onEdit={openEdit}
              onNew={openNew}
              onDelete={deleteTemplate}
              onDuplicate={openDuplicate}
              onBulkSeed={openBulkSeed}
              onManagePrograms={setProgramsManagerCampus}
              onReorder={reorderTemplates}
              knownPrograms={programsForCampus(campus)}
              managedProgramsCount={managedPrograms.filter((p) => p.campus === campus).length}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Programs Manager Dialog */}
      {programsManagerCampus && (
        <ProgramsManagerDialog
          open={!!programsManagerCampus}
          onClose={() => setProgramsManagerCampus(null)}
          campus={programsManagerCampus}
          campusLabel={t(`lead_campus_${programsManagerCampus}`)}
          onChanged={load}
        />
      )}

      {/* Editor Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) closeEditor(); }}>
        <DialogContent className="max-w-[min(1400px,95vw)] max-h-[95vh] overflow-hidden flex flex-col" data-testid="template-editor-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing?.__new ? t("template_new") : t("template_edit")}
              {" — "}
              {t(`lead_campus_${draftCampus}`)}
              {draftProgram ? (
                <span className="text-slate-500 text-sm font-normal ml-2">· {draftProgram}</span>
              ) : (
                <span className="text-slate-500 text-sm font-normal ml-2">· {t("template_campus_fallback")}</span>
              )}
            </DialogTitle>
            <DialogDescription>{t("template_variables_note")}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-0">
            {/* LEFT: Editor — independent scroll */}
            <div className="space-y-4 overflow-y-auto pr-2 pb-2 min-h-0">
              {editing?.__new && (
                <div>
                  <Label htmlFor="tpl-program">{t("template_program_label")}</Label>
                  <Input
                    id="tpl-program"
                    list="observed-programs"
                    value={draftProgram}
                    onChange={(e) => setDraftProgram(e.target.value)}
                    placeholder={t("template_program_placeholder")}
                    className="mt-1"
                    data-testid="template-program-input"
                  />
                  <datalist id="observed-programs">
                    {programsForCampus(draftCampus).map((p) => (
                      <option key={p} value={p} />
                    ))}
                  </datalist>
                  <div className="text-xs text-slate-500 mt-1">{t("template_program_hint")}</div>
                </div>
              )}
              <div>
                <Label htmlFor="tpl-subject">{t("template_subject")}</Label>
                <Input
                  id="tpl-subject"
                  value={draftSubject}
                  onChange={(e) => setDraftSubject(e.target.value)}
                  placeholder="¡Hola {name}! ..."
                  className="mt-1"
                  data-testid="template-subject-input"
                />
              </div>
              <div>
                <Label>{t("template_body")}</Label>
                <div className="mt-1">
                  <RichTextEditor
                    value={draftBody}
                    onChange={setDraftBody}
                    placeholder={t("template_body_placeholder")}
                    testId="template-body-editor"
                  />
                </div>
              </div>
            </div>
            {/* RIGHT: Live preview — sticky, independent scroll */}
            <div className="flex flex-col min-h-0">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                <Eye className="h-3.5 w-3.5" />
                {t("editor_live_preview")}
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-100 flex-1 min-h-0">
                <iframe
                  srcDoc={livePreviewHtml}
                  title="Live preview"
                  className="w-full h-full bg-white"
                  sandbox=""
                  data-testid="template-live-preview"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2 pt-3 border-t border-slate-100">
            <Button variant="outline" onClick={closeEditor}>{t("template_cancel")}</Button>
            <Button onClick={saveTemplate} disabled={saving} data-testid="template-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
              {t("template_save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-preview-dialog">
          <DialogHeader>
            <DialogTitle>{t("template_preview")}</DialogTitle>
            <DialogDescription>{previewSubject}</DialogDescription>
          </DialogHeader>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <iframe srcDoc={previewHtml} title="Email preview" className="w-full h-[600px] bg-white" sandbox="" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>{t("template_cancel")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Seed Dialog */}
      <Dialog open={!!bulkSeedCampus} onOpenChange={(open) => { if (!open) setBulkSeedCampus(null); }}>
        <DialogContent data-testid="bulk-seed-dialog">
          <DialogHeader>
            <DialogTitle>{t("template_bulk_seed_title")}</DialogTitle>
            <DialogDescription>{t("template_bulk_seed_desc")}</DialogDescription>
          </DialogHeader>
          <textarea
            value={bulkSeedText}
            onChange={(e) => setBulkSeedText(e.target.value)}
            placeholder={t("template_bulk_seed_placeholder")}
            rows={10}
            className="w-full rounded-md border border-slate-200 p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-blue/50"
            data-testid="bulk-seed-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkSeedCampus(null)}>{t("template_cancel")}</Button>
            <Button onClick={runBulkSeed} disabled={bulkSeedRunning || !bulkSeedText.trim()} data-testid="bulk-seed-run">
              {bulkSeedRunning ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
              {t("template_bulk_seed_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CampusSection({ campus, items, onEdit, onNew, onDelete, onDuplicate, onBulkSeed, onManagePrograms, onReorder, knownPrograms, managedProgramsCount, t }) {
  const campusDefault = items.find((it) => !it.program);
  const programTemplates = items.filter((it) => !!it.program);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const currentIds = programTemplates.map((t) => t.id);
    const oldIndex = currentIds.indexOf(active.id);
    const newIndex = currentIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const nextIds = arrayMove(currentIds, oldIndex, newIndex);
    // Parent Templates page owns the state — it will optimistically reorder
    // `items` and then persist to the server.
    onReorder?.(campus, nextIds);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`campus-section-${campus}`}>
      <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-slate-600" />
          <h3 className="font-semibold text-slate-900">{t(`lead_campus_${campus}`)}</h3>
          <Badge variant="outline" className="text-xs">
            {programTemplates.length} {t("template_program_count")}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onManagePrograms?.(campus)}
            data-testid={`manage-programs-${campus}`}
            title={t("programs_manager_button_hint")}
          >
            <ListChecks className="h-3.5 w-3.5 mr-1.5" />
            {t("programs_manager_button")}
            {managedProgramsCount > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-4 px-1.5 text-[10px]">{managedProgramsCount}</Badge>
            )}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onBulkSeed(campus)} data-testid={`bulk-seed-${campus}`}>
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            {t("template_bulk_seed")}
          </Button>
          <Button
            size="sm"
            onClick={() => onNew(campus)}
            data-testid={`add-program-tpl-${campus}`}
            className="bg-brand-blue hover:bg-brand-blue/90"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            {t("template_add_program")}
          </Button>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {/* Campus fallback template */}
        <TemplateRow
          tpl={campusDefault}
          label={t("template_campus_fallback")}
          sublabel={t("template_campus_fallback_hint")}
          fallback
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          t={t}
          campus={campus}
        />
        {/* Program-specific templates (drag-and-drop reorderable) */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={programTemplates.map((t) => t.id)} strategy={verticalListSortingStrategy}>
            {programTemplates.map((tpl) => (
              <SortableTemplateRow
                key={tpl.id}
                tpl={tpl}
                label={tpl.program}
                sublabel={t("template_program_specific")}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                t={t}
                campus={campus}
              />
            ))}
          </SortableContext>
        </DndContext>
        {programTemplates.length === 0 && (
          <div className="px-4 py-3 text-xs text-slate-400 italic">
            {t("template_no_program_templates").replace("{campus}", t(`lead_campus_${campus}`))}
          </div>
        )}
      </div>
    </div>
  );
}

function SortableTemplateRow(props) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: props.tpl.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : "auto",
    opacity: isDragging ? 0.8 : 1,
    background: isDragging ? "#f1f5f9" : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div
        {...attributes}
        {...listeners}
        className="absolute left-1 top-1/2 -translate-y-1/2 p-1 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 z-10"
        title={props.t("template_drag_hint") || "Arrastra para reordenar"}
        data-testid={`drag-handle-${props.tpl.id}`}
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <div className="pl-6">
        <TemplateRow {...props} />
      </div>
    </div>
  );
}

function TemplateRow({ tpl, label, sublabel, fallback, onEdit, onDuplicate, onDelete, t }) {
  const plainBody = (tpl?.body_html || "").replace(/<[^>]*>/g, " ").substring(0, 160);
  return (
    <div className="px-4 py-3 flex items-start justify-between gap-3 hover:bg-slate-50 transition-colors" data-testid={`template-row-${tpl?.id || 'default-' + tpl?.campus}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {fallback ? <Layers className="h-4 w-4 text-slate-400" /> : <Mail className="h-4 w-4 text-slate-400" />}
          <div className="font-medium text-sm text-slate-800 truncate">{label}</div>
          {tpl?.is_default ? (
            <Badge variant="outline" className="text-slate-500 text-[10px]">{t("template_is_default")}</Badge>
          ) : (
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{t("template_is_custom")}</Badge>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{sublabel}</div>
        <div className="text-xs text-slate-600 mt-1 line-clamp-1">
          <span className="text-slate-400">{t("template_subject")}: </span>{tpl?.subject}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{plainBody}…</div>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <Button size="sm" variant="outline" onClick={() => onEdit(tpl)} title={t("template_edit")} data-testid={`edit-tpl-${tpl?.id || tpl?.campus}`}>
          <Edit3 className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDuplicate(tpl)} title={t("template_duplicate")} data-testid={`dup-tpl-${tpl?.id || tpl?.campus}`}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
        {tpl?.id && !tpl?.is_default && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50" data-testid={`del-tpl-${tpl.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("template_delete")}</AlertDialogTitle>
                <AlertDialogDescription>{t("template_delete_confirm")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("template_cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(tpl)} className="bg-rose-600 hover:bg-rose-700" data-testid={`del-tpl-confirm-${tpl.id}`}>
                  {t("template_delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}
